import { Link, useNavigate, useParams } from "react-router";
import {
  PopulatedVotingSession,
  PopulatePartyOpinion,
  StudentOpinion,
} from "../../types";
import { useEffect, useMemo, useState } from "react";
import { translate } from "../../translations/index";
import { PartyAnswers } from "../teacher/statement_result";
import APIclient from "../../api-client";

export default function StatementResult({
  votingSession,
  answers,
}: {
  votingSession: PopulatedVotingSession;
  answers: Array<StudentOpinion>;
}) {
  const { index } = useParams();
  const indexAsNumber = Number(index);
  const navigate = useNavigate();
  const statement = votingSession.statements[indexAsNumber - 1];
  const [partyAnswers, setPartyAnswers] = useState<Array<PopulatePartyOpinion>>(
    [],
  );

  useEffect(() => {
    APIclient.sessions
      .partyOpinions(votingSession.code, statement._id)
      .then(setPartyAnswers);
  }, [statement]);

  if (statement === undefined) {
    navigate("..");
  }

  const [answer, opinion] = useMemo(() => {
    const answer = answers.find((a) => a.statementId === statement._id);
    if (answer === undefined) return [null, null];

    const opinion = statement.opinionOptions.find(
      (o) => o.opinion === answer.opinion,
    );
    return [answer, opinion];
  }, [statement, answers]);

  return (
    <>
      <div className="voting-session__content voting-session__content--small">
        <Link to="../results" className="voting-session__back">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="currentcolor"
            className="voting-session__back-icon"
          >
            <path d="M10 18L4 12L10 6L11.4 7.45L7.85 11H20V13H7.85L11.4 16.55L10 18Z" />
          </svg>
          {translate("student.results.backToOverview")}
        </Link>
      </div>
      <h1 className="voting-session__title">
        <span className="--highlighted">
          {translate("student.results.title")}
        </span>
      </h1>

      <div className="voting-session__content">
        <div className="session-arguments">
          <h2 className="session-arguments__title">{statement.description}</h2>
          <details
            className="session-arguments__item session-arguments__item--grey party-answer"
            data-disabled={answer?.comment === undefined}
          >
            <summary className="party-answer__summary">
              {translate("student.results.details.yourAnswer")}

              <span
                className="badge badge--opinion party-answer__badge"
                style={{ "--option-color": opinion?.color || "" }}
              >
                {answer?.opinion || translate("student.results.notAnswered")}
              </span>
            </summary>
            {answer?.comment}
          </details>
        </div>

        <div className="voting-session__options">
          {statement.opinionOptions.map((option) => (
            <PartyAnswers
              option={option}
              partyAnswers={partyAnswers}
              key={option.opinion}
              itemClass="session-arguments__item--grey"
            />
          ))}
        </div>
      </div>

      <div className="voting-session__controls">
        {indexAsNumber > 1 && (
          <Link
            to={`../results/${indexAsNumber - 1}`}
            className="button button--secondary voting-session__control"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentcolor"
              className="button__icon"
            >
              <path d="M7.825 13H20V11H7.825L13.425 5.4L12 4L4 12L12 20L13.425 18.6L7.825 13Z" />
            </svg>
          </Link>
        )}
        <div className="voting-session__progress">
          <span className="voting-session__progress-current">
            {indexAsNumber}{" "}
          </span>
          / {votingSession.statements.length}
        </div>
        {votingSession.statements.length > indexAsNumber && (
          <Link
            to={`../results/${indexAsNumber + 1}`}
            className="button button--secondary voting-session__control voting-session__control--end"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentcolor"
              className="button__icon"
            >
              <path d="M16.175 13H4V11H16.175L10.575 5.4L12 4L20 12L12 20L10.575 18.6L16.175 13Z" />
            </svg>
          </Link>
        )}
      </div>
    </>
  );
}
